import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { LogOut, Plus, Trash2, Link as LinkIcon, Copy, BarChart3, Building2, Coins, Users, DollarSign, FileCheck, Database, Settings } from "lucide-react";
import ApproveHoursTab from "./ApproveHoursTab";
import BlockchainTab from "./BlockchainTab";
import CustomersTab from "./CustomersTab";
import PaymentsTab from "./PaymentsTab";
import UsersTab from "./UsersTab";
import ApplicationsTab from "./ApplicationsTab";
import BackupManager from "./BackupManager";
import { COUNTRIES, getCountiesForCountry } from "../constants/locations";
import "../dashboard.css";

const AdminDashboard = ({ user, logout }) => {
  const [clubs, setClubs] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("clubs");
  const [platformSettings, setPlatformSettings] = useState({ token_price: 5, currency: "GBP" });
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    street_address: "",
    city: "",
    county: "",
    eircode: "",
    country: "Republic of Ireland",
    affiliated_body: "",
    year_established: new Date().getFullYear(),
    club_type: ""
  });
  
  const [availableCounties, setAvailableCounties] = useState(getCountiesForCountry("Republic of Ireland"));

  const baseUrl = window.location.origin;

  useEffect(() => {
    fetchClubs();
    fetchPlatformSettings();
  }, []);

  const fetchClubs = async () => {
    try {
      const response = await axios.get(`${API}/clubs`);
      console.log("Fetched clubs:", response.data);
      setClubs(response.data);
    } catch (error) {
      console.error("Failed to fetch clubs:", error);
      toast.error("Failed to fetch clubs");
    }
  };

  const fetchPlatformSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings`);
      setPlatformSettings(response.data);
    } catch (error) {
      console.error("Failed to fetch platform settings:", error);
    }
  };

  const handleUpdateSettings = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/admin/settings`, platformSettings);
      toast.success("Settings updated successfully!");
      setSettingsDialogOpen(false);
    } catch (error) {
      toast.error("Failed to update settings");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClub = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/clubs`, formData);
      toast.success("Club created successfully with GPS coordinates!");
      setDialogOpen(false);
      setFormData({
        name: "",
        street_address: "",
        city: "",
        county: "",
        eircode: "",
        country: "Republic of Ireland",
        affiliated_body: "",
        year_established: new Date().getFullYear(),
        club_type: ""
      });
      setAvailableCounties(getCountiesForCountry("Republic of Ireland"));
      fetchClubs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create club");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClub = async (clubId) => {
    if (!window.confirm("Are you sure you want to delete this club?")) return;

    try {
      await axios.delete(`${API}/clubs/${clubId}`);
      toast.success("Club deleted successfully!");
      fetchClubs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete club");
    }
  };


  const handleMigration = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/migrate-club-tokens`);
      toast.success(response.data.message);
      fetchClubs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Migration failed");
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateLinks = async (clubId) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/clubs/${clubId}/generate-signup-links`);
      toast.success("Signup links generated successfully!");
      fetchClubs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate signup links");
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text, type) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        toast.success(`${type} signup link copied to clipboard!`);
        return;
      }
    } catch (err) {
      console.log("Modern clipboard API failed, trying fallback:", err);
    }
    
    try {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      const successful = document.execCommand('copy');
      textArea.remove();
      
      if (successful) {
        toast.success(`${type} signup link copied to clipboard!`);
      } else {
        throw new Error("Copy command failed");
      }
    } catch (err) {
      console.error("All copy methods failed:", err);
      prompt(`Copy this ${type} signup link:`, text);
    }
  };

  return (
    <div className="dashboard-container gradient-red">
      {/* Header */}
      <header className="dashboard-header">
        <div className="dashboard-header-content">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_volunteer-hub-35/artifacts/5ta1s578_playback%20logo.jpg" 
                alt="Logo" 
                className="w-12 h-12 rounded-lg"
              />
              <div>
                <h1 className="text-2xl font-black text-red-600 brand-font leading-none">PlayBack Token</h1>
                <p className="text-xs text-gray-500 mt-0.5">Admin Panel</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-semibold text-gray-700">{user.first_name} {user.last_name}</p>
                <p className="text-xs text-gray-500 capitalize">{user.role}</p>
              </div>
              <Button variant="outline" onClick={logout} data-testid="logout-button" className="border-red-300 text-red-600 hover:bg-red-50 btn-modern">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="dashboard-main">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="mb-4 space-y-2">
            {/* First Row - 4 tabs */}
            <TabsList className="modern-tabs-list grid grid-cols-2 sm:grid-cols-4">
              <TabsTrigger value="applications" data-testid="tab-applications" className="modern-tab-trigger">
                <FileCheck className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Applications</span>
              </TabsTrigger>
              <TabsTrigger value="clubs" data-testid="tab-clubs" className="modern-tab-trigger">
                <Building2 className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Clubs</span>
              </TabsTrigger>
              <TabsTrigger value="users" data-testid="tab-users" className="modern-tab-trigger">
                <Users className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Users</span>
              </TabsTrigger>
              <TabsTrigger value="approve" data-testid="tab-approve" className="modern-tab-trigger">
                <BarChart3 className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Approve</span>
              </TabsTrigger>
            </TabsList>
            
            {/* Second Row - 5 tabs */}
            <TabsList className="modern-tabs-list grid grid-cols-2 sm:grid-cols-5">
              <TabsTrigger value="blockchain" data-testid="tab-blockchain" className="modern-tab-trigger">
                <Coins className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Blockchain</span>
              </TabsTrigger>
              <TabsTrigger value="customers" data-testid="tab-customers" className="modern-tab-trigger">
                <Users className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Customers</span>
              </TabsTrigger>
              <TabsTrigger value="payments" data-testid="tab-payments" className="modern-tab-trigger">
                <DollarSign className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Payments</span>
              </TabsTrigger>
              <TabsTrigger value="backups" data-testid="tab-backups" className="modern-tab-trigger">
                <Database className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Backups</span>
              </TabsTrigger>
              <TabsTrigger value="settings" data-testid="tab-settings" className="modern-tab-trigger">
                <Settings className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Settings</span>
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="applications" className="space-y-4">
            <ApplicationsTab />
          </TabsContent>

          <TabsContent value="clubs" className="space-y-4">
        
        {clubs.length > 0 && clubs.some(club => !club.signup_token || !club.volunteer_signup_token) && (
          <Card className="border-amber-300 bg-amber-50">
            <CardContent className="p-3.5">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-200 rounded-full flex items-center justify-center flex-shrink-0">
                    ⚠️
                  </div>
                  <div>
                    <p className="font-semibold text-amber-800 text-sm">Some clubs are missing signup tokens</p>
                    <p className="text-xs text-amber-700">Click to add signup links to existing clubs</p>
                  </div>
                </div>
                <Button
                  onClick={handleMigration}
                  className="bg-amber-600 hover:bg-amber-700 text-white btn-modern"
                  disabled={loading}
                  size="sm"
                >
                  {loading ? "Updating..." : "Fix Tokens"}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
        
        <Card className="enhanced-card">
          <CardHeader className="enhanced-card-header">
            <CardTitle className="enhanced-card-title">Manage Clubs</CardTitle>
            <CardDescription className="enhanced-card-description">Share signup links with supervisors and volunteers</CardDescription>
          </CardHeader>
          <CardContent className="enhanced-card-content">
            {clubs.length === 0 ? (
              <div className="empty-state">
                <LinkIcon className="empty-state-icon" />
                <p className="empty-state-title">No clubs yet</p>
                <p className="empty-state-description">Clubs are created through the application review process</p>
              </div>
            ) : (
              <div className="space-y-3">
                {clubs.map((club) => (
                  <div key={club.id} data-testid={`club-${club.id}`} className="data-item">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="text-lg font-bold text-gray-800">{club.name}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          {club.stripe_connect_account_id ? (
                            club.stripe_payouts_enabled ? (
                              <span data-testid={`stripe-status-${club.id}`} className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5">
                                <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                                Stripe Connected
                              </span>
                            ) : (
                              <span data-testid={`stripe-status-${club.id}`} className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                                <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                                Stripe Onboarding Incomplete
                              </span>
                            )
                          ) : (
                            <span data-testid={`stripe-status-${club.id}`} className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 bg-gray-50 border border-gray-200 rounded-full px-2 py-0.5">
                              <span className="w-1.5 h-1.5 rounded-full bg-gray-400"></span>
                              No Stripe Account
                            </span>
                          )}
                        </div>
                      </div>
                      <Button
                        onClick={() => handleDeleteClub(club.id)}
                        data-testid={`delete-club-${club.id}`}
                        variant="outline"
                        size="sm"
                        className="border-red-300 text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    <div className="space-y-2.5">
                      {club.signup_token && club.volunteer_signup_token ? (
                        <>
                          <div className="bg-amber-50 border border-amber-300 rounded-lg p-3">
                            <div className="flex items-center justify-between mb-2">
                              <Label className="text-xs font-semibold text-amber-700 flex items-center gap-2">
                                <span className="bg-amber-200 text-amber-800 text-xs px-2 py-0.5 rounded">SUPERVISOR</span>
                              </Label>
                              <Button
                                onClick={() => copyToClipboard(`${baseUrl}/signup/supervisor/${club.signup_token}`, "Supervisor")}
                                data-testid={`copy-supervisor-link-${club.id}`}
                                variant="ghost"
                                size="sm"
                                className="text-amber-600 hover:text-amber-700 h-7"
                              >
                                <Copy className="w-3.5 h-3.5 mr-1" />
                                Copy
                              </Button>
                            </div>
                            <input
                              type="text"
                              readOnly
                              value={`${baseUrl}/signup/supervisor/${club.signup_token}`}
                              onClick={(e) => e.target.select()}
                              className="w-full text-xs text-gray-700 font-mono bg-white p-2 rounded border border-amber-200 break-all cursor-pointer"
                            />
                          </div>
                          
                          <div className="bg-red-50 border border-red-300 rounded-lg p-3">
                            <div className="flex items-center justify-between mb-2">
                              <Label className="text-xs font-semibold text-red-700 flex items-center gap-2">
                                <span className="bg-red-200 text-red-800 text-xs px-2 py-0.5 rounded">VOLUNTEER</span>
                              </Label>
                              <Button
                                onClick={() => copyToClipboard(`${baseUrl}/signup/volunteer/${club.volunteer_signup_token}`, "Volunteer")}
                                data-testid={`copy-volunteer-link-${club.id}`}
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:text-red-700 h-7"
                              >
                                <Copy className="w-3.5 h-3.5 mr-1" />
                                Copy
                              </Button>
                            </div>
                            <input
                              type="text"
                              readOnly
                              value={`${baseUrl}/signup/volunteer/${club.volunteer_signup_token}`}
                              onClick={(e) => e.target.select()}
                              className="w-full text-xs text-gray-700 font-mono bg-white p-2 rounded border border-red-200 break-all cursor-pointer"
                            />
                          </div>
                        </>
                      ) : (
                        <div className="bg-gray-50 border border-gray-300 rounded-lg p-3 text-center">
                          <p className="text-gray-600 text-sm mb-2">Signup links not yet generated</p>
                          <Button
                            onClick={() => handleGenerateLinks(club.id)}
                            className="bg-blue-600 hover:bg-blue-700 text-white btn-modern"
                            disabled={loading}
                            size="sm"
                          >
                            {loading ? "Generating..." : "Generate Links"}
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
          </TabsContent>

          <TabsContent value="users" className="space-y-4">
            <UsersTab />
          </TabsContent>

          <TabsContent value="approve" className="space-y-4">
            <ApproveHoursTab user={user} />
          </TabsContent>

          <TabsContent value="blockchain" className="space-y-4">
            <BlockchainTab />
          </TabsContent>

          <TabsContent value="customers" className="space-y-4">
            <CustomersTab />
          </TabsContent>

          <TabsContent value="payments" className="space-y-4">
            <PaymentsTab />
          </TabsContent>

          <TabsContent value="backups" className="space-y-4">
            <BackupManager />
          </TabsContent>

          <TabsContent value="settings" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="w-5 h-5" />
                  Platform Settings
                </CardTitle>
                <CardDescription>Configure global platform settings</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Token Pricing */}
                <div className="border rounded-lg p-4 space-y-4">
                  <h3 className="font-semibold text-lg">Token Pricing</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Price per Token</Label>
                      <div className="flex gap-2">
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={platformSettings.token_price}
                          onChange={(e) => setPlatformSettings({
                            ...platformSettings,
                            token_price: parseFloat(e.target.value) || 0
                          })}
                          className="flex-1"
                        />
                        <Select 
                          value={platformSettings.currency} 
                          onValueChange={(value) => setPlatformSettings({
                            ...platformSettings,
                            currency: value
                          })}
                        >
                          <SelectTrigger className="w-24">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="GBP">GBP</SelectItem>
                            <SelectItem value="EUR">EUR</SelectItem>
                            <SelectItem value="USD">USD</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <p className="text-xs text-gray-500">
                        This price will be shown to all customers when purchasing tokens
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={handleUpdateSettings}
                    disabled={loading}
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >
                    {loading ? "Saving..." : "Save Settings"}
                  </Button>
                </div>

                {/* Info Card */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-800 mb-2">How Token Purchases Work</h4>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>• Companies see total available tokens and can purchase any quantity</li>
                    <li>• You can set minimum purchase requirements per company in the Customers tab</li>
                    <li>• When purchased, the oldest volunteer hours (by activity date) are allocated first</li>
                    <li>• 90% goes to clubs, 10% platform fee</li>
                  </ul>
                </div>

                {/* Test Data Management */}
                <div className="border-2 border-orange-200 rounded-lg p-4 mt-6">
                  <h3 className="font-semibold text-lg text-orange-800 mb-3">Test Data Management</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Manage test data separately from real production data. Test data is flagged with <code className="bg-gray-100 px-1 rounded">is_test: true</code>.
                  </p>
                  
                  <div className="flex flex-wrap gap-3">
                    <Button
                      onClick={async () => {
                        if (!window.confirm("This will flag ALL existing volunteer hours, purchases, and related data as TEST data. Continue?")) return;
                        setLoading(true);
                        try {
                          const response = await axios.post(`${API}/admin/flag-existing-as-test`);
                          toast.success(`Flagged as test: ${response.data.results.volunteer_hours_flagged} hours, ${response.data.results.purchases_flagged} purchases`);
                        } catch (error) {
                          toast.error("Failed to flag test data");
                        } finally {
                          setLoading(false);
                        }
                      }}
                      disabled={loading}
                      variant="outline"
                      className="border-orange-400 text-orange-700 hover:bg-orange-50"
                    >
                      Flag Existing Data as Test
                    </Button>
                    
                    <Button
                      onClick={async () => {
                        if (!window.confirm("⚠️ WARNING: This will PERMANENTLY DELETE all test data (flagged with is_test: true). Real data will NOT be affected. Continue?")) return;
                        if (!window.confirm("Are you absolutely sure? This cannot be undone.")) return;
                        setLoading(true);
                        try {
                          const response = await axios.delete(`${API}/admin/clear-test-data`);
                          toast.success(`Deleted: ${response.data.results.volunteer_hours_deleted} hours, ${response.data.results.purchases_deleted} purchases`);
                        } catch (error) {
                          toast.error("Failed to clear test data");
                        } finally {
                          setLoading(false);
                        }
                      }}
                      disabled={loading}
                      variant="outline"
                      className="border-red-400 text-red-700 hover:bg-red-50"
                    >
                      Clear Test Data
                    </Button>
                    
                    <Button
                      onClick={async () => {
                        try {
                          const response = await axios.get(`${API}/admin/test-data-stats`);
                          const stats = response.data;
                          alert(`Test Data Stats:\n\nVolunteer Hours:\n  Test: ${stats.volunteer_hours.test}\n  Real: ${stats.volunteer_hours.real}\n\nPurchases:\n  Test: ${stats.purchases.test}\n  Real: ${stats.purchases.real}`);
                        } catch (error) {
                          toast.error("Failed to get stats");
                        }
                      }}
                      variant="outline"
                      className="border-gray-400 text-gray-700 hover:bg-gray-50"
                    >
                      View Test Data Stats
                    </Button>
                  </div>
                  
                  <p className="text-xs text-gray-500 mt-3">
                    💡 Tip: Flag existing data as test before real customers start using the platform. New data from real users will NOT be flagged.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AdminDashboard;
